import { SPACE_TYPE_LABELS, type StorageQuote } from '@/shared/storage-api';
import { formatShortDate, montevideoDateKey } from '@/shared/format';

/**
 * Quote detail page (2026-10-28) — what Mis cotizaciones' rows and the quote's own page
 * (`CotizacionDetailPage`) both say about a quote, lifted out of `CotizacionesPage`.
 */

/** "Solicitud #id · size" — `/quotes/mine` sends no volume, so the size is the space label. */
export function quoteTitle(quote: StorageQuote): string {
  return quote.request
    ? `Solicitud #${quote.storageRequestId} · ${SPACE_TYPE_LABELS[quote.request.spaceType] ?? quote.request.spaceType}`
    : `Solicitud #${quote.storageRequestId}`;
}

/**
 * Whole calendar days from today to `validUntil`, both read as Montevideo calendar days.
 * NEX-1165 (W2-hard-a) — `validUntil` is the last instant of a day in America/Montevideo
 * (nexvia-api-web `domain/quote-defaults.ts`: "2026-12-31" comes back as
 * "2027-01-01T02:59:59.999Z"), so its UTC date part is the NEXT day: the old split('T')[0] read
 * counted one day too many. Diffing the two `YYYY-MM-DD` keys as UTC midnights is exact (no DST).
 * `Date.now()` (not `new Date()`) only so a test can pin "now" with a plain
 * `vi.spyOn(Date, 'now')`, no fake-timers.
 */
function daysUntil(validUntil: string): number {
  const toUtcMidnight = (key: string) => {
    const [year, month, day] = key.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  const target = toUtcMidnight(montevideoDateKey(new Date(validUntil)));
  const today = toUtcMidnight(montevideoDateKey(new Date(Date.now())));
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

/**
 * Quote detail page (2026-10-28, final review) — the list row no longer carries the guardado, so
 * its status detail says where the guardado is, and above all when the depósito has to act: while
 * contracted, the reception only it can confirm (the payout waits on it).
 */
type StorageStatus = NonNullable<NonNullable<StorageQuote['request']>['storageStatus']>;

const GUARDADO_DETAILS: Record<StorageStatus, string> = {
  contracted: 'Confirmá la recepción',
  active: 'Guardado activo',
  ending: 'Guardado terminando',
  ended: 'Guardado terminado'
};

function guardadoDetail(quote: StorageQuote): string | null {
  const storageStatus = quote.request?.storageStatus;
  return storageStatus ? GUARDADO_DETAILS[storageStatus] : null;
}

/**
 * The Claude Design mockup's one-line status detail beside each quote's chip — every case reads a
 * field already on `StorageQuote`, no backend change. `cancelled` has no mockup line (omitted);
 * `pending` also omits it whenever `validUntil` is missing or already passed, rather than show a
 * stale or negative countdown. `rejected` reads as "the client took a different depósito's quote
 * for this same request", not a quality judgement on this one — confirmed against nexvia-api-web's
 * `rejectOtherPendingQuotes` flow, the thing that actually flips a sibling quote to `rejected` once
 * another one on the same request is accepted.
 */
export function quoteStatusDetail(quote: StorageQuote): string | null {
  switch (quote.status) {
    case 'pending': {
      if (!quote.validUntil || Number.isNaN(new Date(quote.validUntil).getTime())) return null;
      const days = daysUntil(quote.validUntil);
      if (days <= 0) return null;
      return `Vence en ${days} ${days === 1 ? 'día' : 'días'}`;
    }
    case 'accepted': {
      const date = formatShortDate(quote.acceptedAt);
      return [date ? `Aceptada el ${date}` : null, guardadoDetail(quote)].filter(Boolean).join(' · ') || null;
    }
    case 'rejected':
      return 'El usuario eligió otro depósito';
    case 'expired':
      return 'Venció sin respuesta';
    default:
      return null;
  }
}
